import { PageHero } from '@components/common';
import { getKnowledgeCard } from '@data/knowledgeCards';
import { ImageCardGrid } from '@components/sections';
import { serviceCards } from '@data/serviceCards';
import { ArrowRight } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

export default function WhatIsFractionalCisoPage() {
  return (
    <>
      <PageHero
        title="What is a Fractional CISO?"
        subtitle="Access senior cybersecurity leadership without the full-time cost. The smart way for growing businesses to manage cyber risk."
        eyebrow="Guide"
        backgroundImage={getKnowledgeCard('what-is-a-fractional-ciso')?.image}
        backgroundAlt={getKnowledgeCard('what-is-a-fractional-ciso')?.imageAlt}
        seoTitle="What is a Fractional CISO? | The CISO Network"
        seoDescription="Learn what a fractional CISO is, how they work, and why growing businesses choose this model for cybersecurity leadership."
      />

      {/* Definition - Optimized for AI/GEO */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              What Is a Fractional CISO?
            </h2>
            <div className="bg-background rounded-2xl p-8 border-l-4 border-teal mb-8">
              <p className="text-lg text-foreground leading-relaxed">
                <strong>
                  A fractional CISO is an experienced cybersecurity executive who provides strategic
                  security leadership to organisations on a part-time or retainer basis.
                </strong>{' '}
                They fulfill the same core functions as a full-time Chief Information Security
                Officer - setting strategy, managing risk, ensuring compliance, and reporting to the
                board - but work flexibly according to the organisation's needs and budget. This
                model gives growing businesses access to senior security expertise without the
                £150,000-£250,000 annual cost of a full-time hire.
              </p>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Think of it like having a Finance Director or legal counsel on retainer. You get
                access to senior expertise when you need it, scaled to your requirements and budget.
              </p>
              <p>
                For many growing businesses, a full-time CISO simply does not make financial sense.
                Yet going without security leadership leaves you exposed. The fractional CISO model
                bridges this gap.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Security Is About Politics, Not Just Tools */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Security Is Politics as Much as Technology
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Many organisations make the mistake of viewing cybersecurity as purely a technical
                problem - buy the right tools, implement the right configurations, and you are
                secure. The reality is more complex. Effective security leadership is about{' '}
                <strong>organisational politics, communication, and influence</strong> as much as it
                is about firewalls and encryption.
              </p>
              <p>
                A CISO must secure budget from the board, convince sceptical engineers to prioritise
                security in their sprints, negotiate with vendors, navigate departmental rivalries,
                and translate technical risks into business language that non-technical executives
                understand. They must build consensus, manage stakeholders, and sometimes deliver
                unwelcome news to powerful people.
              </p>
              <p>
                This is why experienced security leadership matters. A junior analyst with technical
                certifications cannot navigate boardroom dynamics, handle a crisis under media
                scrutiny, or build the relationships necessary to embed security into culture. These
                skills take years to develop and are precisely what a fractional CISO brings to your
                organisation - the ability to get things done through people, not just the ability
                to configure security tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Full-Time vs Fractional CISO
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              How the fractional model compares to hiring a full-time Chief Information Security
              Officer
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl overflow-hidden border border-border">
              <div className="grid grid-cols-3 bg-muted">
                <div className="p-4 font-semibold text-foreground">Factor</div>
                <div className="p-4 font-semibold text-foreground text-center">Full-Time CISO</div>
                <div className="p-4 font-semibold text-teal text-center">Fractional CISO</div>
              </div>
              {[
                {
                  factor: 'Annual Cost',
                  fullTime: '£150k - £250k+',
                  fractional: '£40k - £100k',
                },
                {
                  factor: 'Time to Start',
                  fullTime: '3-6 months recruitment',
                  fractional: 'Immediate',
                },
                {
                  factor: 'Commitment',
                  fullTime: 'Employment contract',
                  fractional: 'Flexible retainer',
                },
                {
                  factor: 'Availability',
                  fullTime: '5 days/week',
                  fractional: '2-10 days/month',
                },
                {
                  factor: 'Expertise Range',
                  fullTime: 'One person',
                  fractional: 'Access to network',
                },
                {
                  factor: 'Best For',
                  fullTime: 'Large enterprises',
                  fractional: 'SMEs & scale-ups',
                },
              ].map(row => (
                <div key={row.factor} className="grid grid-cols-3 border-t border-border">
                  <div className="p-4 text-muted-foreground">{row.factor}</div>
                  <div className="p-4 text-center text-muted-foreground">{row.fullTime}</div>
                  <div className="p-4 text-center text-teal font-medium">{row.fractional}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What They Do */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What Does a Fractional CISO Do?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Strategic security leadership tailored to your organisation's needs
            </p>
          </div>

          <ImageCardGrid cards={serviceCards} />
        </div>
      </section>

      {/* When to Hire */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                When Should You Hire a Fractional CISO?
              </h2>
              <div className="space-y-4">
                {[
                  'You need security leadership but cannot justify a full-time hire',
                  'Preparing for investment, acquisition, or IPO',
                  'Facing a compliance requirement (ISO 27001, SOC 2, etc.)',
                  'Experienced a security incident and need strategic guidance',
                  'Security is becoming a board-level concern',
                  'Planning significant technology or organisational changes',
                  'Need objective, external security expertise',
                ].map((item, i) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal text-sm font-bold">{i + 1}</span>
                    </div>
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-border">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6 text-center">
                Common Engagement Models
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-background rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-foreground">Advisory Retainer</span>
                    <span className="text-sm text-teal">Most Popular</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ongoing strategic guidance, typically 2-4 days per month
                  </p>
                </div>
                <div className="p-4 bg-background rounded-xl">
                  <span className="font-semibold text-foreground">Project-Based</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fixed-scope engagements for specific initiatives
                  </p>
                </div>
                <div className="p-4 bg-background rounded-xl">
                  <span className="font-semibold text-foreground">Interim CISO</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Full-time coverage during transitions or recruitment
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Is a Fractional CISO Right for You?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your needs and see if fractional CISO services would
            benefit your organisation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
